import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import API from "../../api/axiosInstance";
import {
  utcToLADateKey,
  formatLADateKeyReadable,
  formatLADateKeyOrRangeForDisplay,
} from "../../utils/laDateUtils";
import "../css/RoomsInformation.css";
import { EmsDateRangeEmphasis } from "./EmsDateRangeEmphasis";

const occupancyForBooking = (b) => b.is_guest_only ? (b.guest_count || 0) : 1 + (b.guest_count || 0);

const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse grouped entry label "YYYY-MM-DD" or "YYYY-MM-DD to YYYY-MM-DD". */
function parseEntryDateLabel(label) {
  if (!label || typeof label !== "string") return null;
  const t = label.trim();
  if (t.includes(" to ")) {
    const [a, b] = t.split(/\s+to\s+/).map((x) => x.trim());
    if (DATE_KEY_RE.test(a) && DATE_KEY_RE.test(b)) return { fromKey: a, toKey: b };
  }
  if (DATE_KEY_RE.test(t)) return { fromKey: t, toKey: t };
  return null;
}

/** Labelled From / To (or single Date) — easier to scan than hyphen-heavy one-liners. */
function BookingDateSummary({ fromKey, toKey, variant = "hero" }) {
  const from = formatLADateKeyReadable(fromKey);
  const to = formatLADateKeyReadable(toKey);
  const single = fromKey === toKey;
  const mod = variant === "compact" ? "compact" : "hero";

  if (single) {
    return (
      <div className={`booking-date-summary booking-date-summary--single booking-date-summary--${mod}`}>
        <span className="booking-date-summary__label">Date</span>
        <span className="booking-date-summary__value">{from}</span>
      </div>
    );
  }

  return (
    <div className={`booking-date-summary booking-date-summary--range booking-date-summary--${mod}`}>
      <div className="booking-date-summary__row">
        <span className="booking-date-summary__label">From</span>
        <span className="booking-date-summary__value">{from}</span>
      </div>
      <div className="booking-date-summary__row">
        <span className="booking-date-summary__label">To</span>
        <span className="booking-date-summary__value">{to}</span>
      </div>
    </div>
  );
}

function personIdFromBooking(b) {
  const p = b.participant_id;
  if (!p) return "";
  return typeof p === "object" && p._id ? String(p._id) : String(p);
}

function competitionIdFromBooking(b, fallback) {
  const c = b.competition_id;
  if (c && typeof c === "object" && c._id) return String(c._id);
  if (c) return String(c);
  return fallback ? String(fallback) : "";
}

function allocationTypeLabelFromBooking(b) {
  const t = b?.allocation_type;
  if (!t) return "";
  if (t === "private") return "Private";
  if (t === "required" || t === "standard") return "Required";
  return String(t);
}

async function sendCancellationEmailRequest({ cancelledBookings, hotelName, roomType, roomLabel, competitionId }) {
  if (!cancelledBookings?.length) return;
  const keys = [...new Set(cancelledBookings.map((b) => b.la_date_key).filter(Boolean))].sort();
  if (!keys.length) return;
  const participant_id = personIdFromBooking(cancelledBookings[0]);
  if (!participant_id) return;
  const competition_id = competitionIdFromBooking(cancelledBookings[0], competitionId) || undefined;
  const guest_count = Math.max(...cancelledBookings.map((b) => Number(b?.guest_count || 0)));
  const allocation_type = cancelledBookings[0]?.allocation_type || "";
  const allocation_type_label = allocationTypeLabelFromBooking(cancelledBookings[0]);
  try {
    await API.post("/ems/room-bookings/send-cancellation", {
      participant_id,
      checkin_date: keys[0],
      checkout_date: keys[keys.length - 1],
      total_nights: keys.length,
      hotel_name: hotelName || "",
      room_type: roomType || "",
      room_label: roomLabel || "",
      guest_count,
      allocation_type,
      allocation_type_label,
      competition_id,
    });
  } catch (err) {
    console.error("Cancellation email failed:", err);
  }
}

const BookingCellMenu = ({
  bookings, unit, date, bedCount, defaultPrice, isPast,
  onClose, onChanged, onAddAnother,
  groupedEntries, dateRange, onEditEntry,
  hotelName = "", roomType = "", competitionId = "",
}) => {
  const isRangeMode = !!groupedEntries;
  const [error, setError] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  const singleDateKey = utcToLADateKey(date);

  const handleDelete = (b) => {
    setConfirmModal({
      message: "Cancel this booking?",
      onConfirm: async () => {
        setIsDeleting(true);
        try {
          await API.delete(`/ems/room-bookings/${b._id}`);
          await sendCancellationEmailRequest({
            cancelledBookings: [b],
            hotelName,
            roomType,
            roomLabel: unit?.room_label || "",
            competitionId,
          });
          onChanged("Booking cancelled successfully.");
        } catch (err) {
          setError(err.response?.data?.message || "Failed to cancel booking");
          setIsDeleting(false);
        }
      },
    });
  };

  const handleDeleteEntry = (entry) => {
    const count = entry.bookings.length;
    setError("");
    setConfirmModal({
      message: `Cancel ${count} booking${count > 1 ? "s" : ""} for this participant?`,
      onConfirm: async () => {
        setIsDeleting(true);
        const slice = [...entry.bookings];
        try {
          for (const b of slice) {
            await API.delete(`/ems/room-bookings/${b._id}`);
          }
          await sendCancellationEmailRequest({
            cancelledBookings: slice,
            hotelName,
            roomType,
            roomLabel: unit?.room_label || "",
            competitionId,
          });
          onChanged(`${count} booking${count > 1 ? "s" : ""} cancelled successfully.`);
        } catch (err) {
          setError(err.response?.data?.message || "Failed to cancel bookings");
          setIsDeleting(false);
        }
      },
    });
  };

  const handleEditSingle = (b) => {
    if (onEditEntry) {
      onEditEntry({
        participant: b.participant_id,
        dateLabel: formatLADateKeyReadable(singleDateKey),
        bookings: [b],
      });
    }
  };

  const allocationLabel = (b) =>
    (b.allocation_type === "standard" || b.allocation_type === "required")
      ? "Required"
      : b.allocation_type;

  const detailLine = (b) =>
    `Type: ${allocationLabel(b)} | Guests: ${occupancyForBooking(b)} | Price: $${b.price != null ? b.price : defaultPrice}`;

  if (confirmModal) {
    return (
      <div className="booking-cell-menu-overlay" role="dialog" aria-modal="true">
        <div className="booking-cell-menu">
          <div className="booking-cell-menu-header">
            <h5 className="booking-cell-menu-title">Confirm Cancellation</h5>
          </div>
          <div className="booking-cell-menu-confirm-body">
            <p className="booking-cell-menu-confirm-message">{confirmModal.message}</p>
            {error && <div className="booking-cell-menu-error">{error}</div>}
          </div>
          <div className="booking-cell-menu-footer">
            <button type="button" className="btn btn-danger btn-sm" onClick={confirmModal.onConfirm} disabled={isDeleting}>
              {isDeleting
                ? <><FontAwesomeIcon icon={faCircleNotch} spin style={{ marginRight: "6px" }} />Cancelling…</>
                : "Yes, Cancel"}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setConfirmModal(null); setError(""); }} disabled={isDeleting}>
              No
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isRangeMode) {
    return (
      <div className="booking-cell-menu-overlay" role="dialog" aria-modal="true" aria-labelledby="booking-cell-menu-title">
        <div className="booking-cell-menu">
          <div className="booking-cell-menu-header">
            <h5 id="booking-cell-menu-title" className="booking-cell-menu-title">Bookings &mdash; Room {unit.room_label}</h5>
            <button type="button" onClick={onClose} className="booking-cell-menu-close" aria-label="Close">&times;</button>
          </div>
          <div className="booking-cell-menu-date booking-cell-menu-date--panel">
            <BookingDateSummary fromKey={dateRange.from} toKey={dateRange.to} variant="hero" />
          </div>
          {error && <div className="booking-cell-menu-error">{error}</div>}

          {groupedEntries.map((entry, idx) => {
            const person = entry.participant;
            const name = person ? `${person.name || ""} ${person.lname || ""}`.trim() : "(Unknown)";
            const b = entry.bookings[0];

            return (
              <div key={idx} className="booking-cell-menu-card">
                <div className="booking-cell-menu-card-top">
                  <strong className="booking-cell-menu-card-name">
                    {name}
                    {b.is_guest_only && <span className="rooms-info-guest-only-badge">Guest only</span>}
                  </strong>
                  <div className="booking-cell-menu-card-actions">
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onEditEntry(entry)}>Edit</button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteEntry(entry)}>Cancel</button>
                  </div>
                </div>
                <div className="booking-cell-menu-card-meta">
                  {(() => {
                    const keys = parseEntryDateLabel(entry.dateLabel);
                    if (keys) {
                      return (
                        <BookingDateSummary
                          fromKey={keys.fromKey}
                          toKey={keys.toKey}
                          variant="compact"
                        />
                      );
                    }
                    return (
                      <EmsDateRangeEmphasis variant="compact">
                        {formatLADateKeyOrRangeForDisplay(entry.dateLabel)}
                      </EmsDateRangeEmphasis>
                    );
                  })()}
                </div>
                <div className="booking-cell-menu-card-details">{detailLine(b)}</div>
              </div>
            );
          })}

          <div className="booking-cell-menu-footer booking-cell-menu-footer--with-add">
            <div>
              {!isPast && onAddAnother && (() => {
                const occupancyByDate = {};
                for (const entry of groupedEntries) {
                  for (const b of entry.bookings) {
                    occupancyByDate[b.la_date_key] = (occupancyByDate[b.la_date_key] || 0) + occupancyForBooking(b);
                  }
                }
                const maxOccupancy = Math.max(...Object.values(occupancyByDate), 0);
                return maxOccupancy < bedCount ? (
                  <button type="button" className="btn btn-sm btn-outline-warning" onClick={onAddAnother}>+ Add Another</button>
                ) : null;
              })()}
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-cell-menu-overlay" role="dialog" aria-modal="true" aria-labelledby="booking-cell-menu-title">
      <div className="booking-cell-menu">
        <div className="booking-cell-menu-header">
          <h5 id="booking-cell-menu-title" className="booking-cell-menu-title">Bookings &mdash; Room {unit.room_label}</h5>
          <button type="button" onClick={onClose} className="booking-cell-menu-close" aria-label="Close">&times;</button>
        </div>
        <div className="booking-cell-menu-date booking-cell-menu-date--panel">
          <BookingDateSummary fromKey={singleDateKey} toKey={singleDateKey} variant="hero" />
        </div>
        {error && <div className="booking-cell-menu-error">{error}</div>}

        {bookings.map((b) => {
          const person = b.participant_id;
          const name = person ? `${person.name || ""} ${person.lname || ""}`.trim() : "(Unknown)";

          return (
            <div key={b._id} className="booking-cell-menu-card">
              <div className="booking-cell-menu-card-top">
                <strong className="booking-cell-menu-card-name">
                  {name}
                  {b.is_guest_only && <span className="rooms-info-guest-only-badge">Guest only</span>}
                </strong>
                <div className="booking-cell-menu-card-actions">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleEditSingle(b)}>
                    {isPast ? "Edit Price" : "Edit"}
                  </button>
                  {!isPast && (
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(b)}>Cancel</button>
                  )}
                </div>
              </div>
              <div className="booking-cell-menu-card-details">{detailLine(b)}</div>
            </div>
          );
        })}

        <div className="booking-cell-menu-footer booking-cell-menu-footer--with-add">
          <div>
            {!isPast && onAddAnother && (() => {
              const currentOccupants = bookings.reduce((s, x) => s + occupancyForBooking(x), 0);
              return currentOccupants < bedCount ? (
                <button type="button" className="btn btn-sm btn-outline-warning" onClick={onAddAnother}>+ Add Another</button>
              ) : null;
            })()}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default BookingCellMenu;
